import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, ExternalLink } from "lucide-react";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

/**
 * Affiliate Program CTA — homepage block.
 * Compliance: FTC disclosure inline. Compliant-marketing language.
 */
const AffiliateProgramCTA = () => {
  return (
    <section className="py-16 bg-white" data-testid="affiliate-program-cta">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#3a2a26] p-8 md:p-14">
          {/* decorative accent */}
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 bg-[#d4a5ad]/15 rounded-full blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#b89a6d]/15 rounded-full blur-3xl"
          />

          <div className="relative grid md:grid-cols-3 gap-8 md:items-center">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur border border-white/20 rounded-full mb-5">
                <DollarSign className="w-3.5 h-3.5 text-[#e8c5b8]" />
                <span className="text-xs uppercase tracking-wider text-[#e8c5b8] font-semibold">
                  Affiliate Partner Program
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                Become an Affiliate.
                <span className="block bg-gradient-to-r from-[#e8c5b8] to-[#d4a5ad] bg-clip-text text-transparent">
                  Earn for you or your company.
                </span>
              </h2>
              <p className="text-base md:text-lg text-[#e8c5b8] max-w-xl leading-relaxed mb-6">
                Promote a curated catalog of research-grade peptides through one tracked link and earn
                commission on every qualifying purchase. Built for med spas, content creators, gyms, and
                wellness brands. Free to join.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href={AFFILIATE_URLS.partnerProgram}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  data-testid="affiliate-cta-apply-btn"
                  onClick={onAffiliateClick({ partner: "paramount-partners", product: "affiliate-program-home-cta", url: AFFILIATE_URLS.partnerProgram })}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
                >
                  Become an Affiliate
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/affiliate-program"
                  data-testid="affiliate-cta-learn-more"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-[#e8c5b8] hover:text-white font-semibold text-sm"
                >
                  How it works <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="space-y-3" data-testid="affiliate-cta-perks">
              <div className="flex items-start gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                <span className="text-2xl">→</span>
                <div>
                  <p className="text-sm font-bold text-white">High-AOV niche</p>
                  <p className="text-xs text-[#e8c5b8]">Premium per-order economics</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                <span className="text-2xl">→</span>
                <div>
                  <p className="text-sm font-bold text-white">Solo or corporate</p>
                  <p className="text-xs text-[#e8c5b8]">One platform, both account paths</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                <span className="text-2xl">→</span>
                <div>
                  <p className="text-sm font-bold text-white">Free to join</p>
                  <p className="text-xs text-[#e8c5b8]">No fees, ever</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AffiliateProgramCTA;
